import * as SecureStore from 'expo-secure-store';

/**
 * Persists a lightweight snapshot of the most recently authenticated user so
 * the login screen can greet a returning user with their name/avatar instead
 * of a blank email field.
 *
 * This is intentionally NOT cleared on sign-out — the whole point is to
 * remember who used the app last. It is stored in SecureStore (not plain
 * AsyncStorage) because it contains the user's email. It is overwritten on the
 * next successful login and can be cleared explicitly via `clear()` (e.g. the
 * "Trocar" / switch-user action or account deletion).
 */
const LAST_USER_KEY = 'last_user_profile';

export interface LastUser {
  email: string;
  name: string | null;
  avatarUrl: string | null;
}

class LastUserService {
  async set(user: LastUser): Promise<void> {
    try {
      if (!user?.email) return;
      await SecureStore.setItemAsync(LAST_USER_KEY, JSON.stringify(user));
    } catch (error) {
      console.warn('⚠️ LastUserService: failed to store last user', error);
    }
  }

  async get(): Promise<LastUser | null> {
    try {
      const value = await SecureStore.getItemAsync(LAST_USER_KEY);
      if (!value) return null;
      const parsed = JSON.parse(value) as LastUser;
      if (!parsed?.email) return null;
      return parsed;
    } catch (error) {
      console.warn('⚠️ LastUserService: failed to read last user', error);
      return null;
    }
  }

  async clear(): Promise<void> {
    try {
      await SecureStore.deleteItemAsync(LAST_USER_KEY);
    } catch (error) {
      console.warn('⚠️ LastUserService: failed to clear last user', error);
    }
  }
}

export const lastUserService = new LastUserService();
